<!-- Base Expiring Contracts Section -->
<template>
  <v-card
    :id="sectionId"
    v-if="isVisible"
    :style="sectionStyle"
    :class="[`${baseClass} mb-6 mt-6`, 'dashboard-section-chip-filter-scroll-target']"
    elevation="0"
  >
    <!-- Header: title, subtitle, timeframe filter -->
    <section-header
      :base-class="baseClass"
      :title="title"
      :subtitle="subtitle"
      :icon="headerIcon"
    >
      <template #controls>
        <slot name="controls">
          <div
            :class="[
              `${baseClass}-filter-control-wrap`,
              'dashboard-section-filter-control-wrap',
              { 'dashboard-section-filter-control-highlight': filterControlHighlightActive },
            ]"
          >
            <!-- Timeframe filter select (default control) -->
            <v-select
              :class="`${baseClass}-timeframe-select`"
              v-model="selectedTimeframe"
              :items="timeframeOptions"
              dense
              outlined
              hide-details
              label="Timeframe"
              :menu-props="{ contentClass: 'timeframe-menu' }"
            />
          </div>
        </slot>
      </template>
    </section-header>

    <!-- Loading state -->
    <v-card-text v-if="isLoading" :class="`${baseClass}-loading text-center py-8`">
      <v-progress-circular indeterminate color="#ff1463"></v-progress-circular>
    </v-card-text>

    <!-- Empty state -->
    <div v-else-if="filteredContracts.length === 0" :class="`${baseClass}-empty text-center py-8 grey--text`">
      {{ emptyMessage }}
    </div>

    <!-- Contracts list -->
    <div v-else>
      <!-- Contract card -->
      <div
        v-for="(contract, index) in paginatedItems"
        :key="`contract-${contract.tender_id}-${index}`"
        :class="{ [`${baseClass}-contract-card-with-border`]: index > 0 }"
      >
        <!-- Card wrapper: router-link to tender -->
        <router-link
          :to="`/tender/${contract.tender_id}`"
          :class="`${baseClass}-contract-card pa-6`"
          :style="getContractCardStyle(contract)"
          style="display: block; text-decoration: none; color: inherit;"
          target="_blank"
        >
          <!-- Card main row: left content + right days left -->
          <div :class="`${baseClass}-contract-card-main-row d-flex justify-space-between align-start mb-4`">
            <!-- Left: title, chips, buyer/supplier/value/expiry -->
            <div :class="`${baseClass}-contract-card flex-grow-1`">
              <!-- Tender title -->
              <div :class="`${baseClass}-contract-card-title`">
                <div :class="`${baseClass}-contract-card-title-link`">
                  {{ contract.title }}
                </div>
              </div>

              <!-- Chips: custom slot for chips -->
              <div :class="`${baseClass}-contract-card-chips d-flex flex-wrap gap-2 mb-1`" @click.stop.prevent>
                <slot name="chips" :contract="contract" :getTimeframeChip="getTimeframeChip" :setTimeframeFromContract="setSelectedTimeframeFromContract">
                  <!-- Default chips: timeframe chip -->
                  <template v-if="getTimeframeChip(contract)">
                    <v-chip
                      x-small
                      :class="`contract-chip ${getTimeframeChip(contract).class}`"
                      style="cursor: pointer;"
                      @click.stop.prevent="setSelectedTimeframeFromContract(contract)"
                    >
                      <v-icon x-small left class="contract-chip-icon">{{ getTimeframeChip(contract).icon }}</v-icon>
                      {{ getTimeframeChip(contract).label }}
                    </v-chip>
                  </template>
                </slot>
              </div>
            </div>

            <!-- Right: days left -->
            <div class="text-right ml-4">
              <div
                :class="[
                  `${baseClass}-contract-card-days-left text-h5 font-weight-bold`,
                  getDaysLeftColor(contract)
                ]"
              >
                {{ contract.days_until_expiry === 0 ? 'Today' : contract.days_until_expiry }}
              </div>
              <div v-if="contract.days_until_expiry === 1" :class="`${baseClass}-expiring-contract-card-days-left-label text-body`">day left</div>
              <div v-else-if="contract.days_until_expiry > 1" :class="`${baseClass}-expiring-contract-card-days-left-label text-body`">days left</div>
            </div>
          </div>

          <!-- High Value alert -->
          <div 
            v-if="contract.value > 500000" 
            :class="`${baseClass}-expiring-contract-alert pa-3 mb-4`"
          >
            <div :class="`${baseClass}-expiring-contract-alert-text text-body-2`">
              <template v-if="contract.value > 1000000">
                High-value opportunity: {{ formatCurrency(contract.value) }} contract expiring soon.
              </template>
              <template v-else>
                Significant-value opportunity: {{ formatCurrency(contract.value) }} contract expiring soon.
              </template>
            </div>
          </div>

          <!-- Contract details grid -->
          <div :class="`${baseClass}-contract-details-container mb-4`">
            <v-row>
              <v-col cols="6" md="3">
                <div :class="`${baseClass}-contract-detail-label text-body mb-1`">Buyer</div>
                <div :class="`${baseClass}-contract-detail-value text-body-2 font-weight-medium`">
                  <router-link 
                    v-if="contract.publisher_id" 
                    :to="`/entity/${contract.publisher_id}`" 
                    :class="`${baseClass}-contract-detail-link text-decoration-none`"
                    target="_blank"
                    @click.stop
                  >
                    {{ contract.publisher_name }}
                  </router-link>
                  <span v-else>{{ contract.publisher_name }}</span>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div :class="`${baseClass}-contract-detail-label text-body mb-1`">Current Holder</div>
                <div :class="`${baseClass}-contract-detail-value text-body-2 font-weight-medium`">
                  <router-link 
                    v-if="contract.supplier_id" 
                    :to="`/supplier/${contract.supplier_id}`" 
                    :class="`${baseClass}-contract-detail-link text-decoration-none`"
                    target="_blank"
                    @click.stop
                  >
                    {{ contract.supplier_name }}
                  </router-link>
                  <span v-else>{{ contract.supplier_name }}</span>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div :class="`${baseClass}-contract-detail-label text-body mb-1`">Contract Value</div>
                <div :class="`${baseClass}-contract-detail-value text-body-2 font-weight-medium`">
                  {{ formatCurrency(contract.value) }}
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div :class="`${baseClass}-contract-detail-label text-body mb-1`">Expires</div>
                <div :class="`${baseClass}-contract-detail-value text-body-2 font-weight-medium`">
                  {{ formatDate(contract.expiry_date) }}
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- Story block (optional) -->
          <div 
            v-if="showStory && buildContractStory(contract)" 
            :class="`${baseClass}-expiring-contract-story-container mb-4`"
          >
            <div :class="`${baseClass}-expiring-contract-story pa-3`">
              <div :class="`${baseClass}-expiring-contract-story-text text-body-2`">
                {{ buildContractStory(contract) }}
              </div>
            </div>
          </div>
        
          <!-- Actions: custom slot for action buttons -->
          <div :class="`${baseClass}-contract-actions d-flex justify-end`">
            <slot name="actions" :contract="contract">
              <!-- Default action button if no slot provided -->
              <v-btn
                small
                outlined
                color="primary"
                :class="`${baseClass}-contract-action-btn text-capitalize body-2`"
                :loading="contract.removing || contract.adding"
                :disabled="contract.removing || contract.adding"
                @click.stop.prevent="handleAction(contract)"
              >
                <v-icon small left>{{ actionButtonIcon }}</v-icon>
                {{ actionButtonText }}
              </v-btn>
            </slot>
          </div>
        </router-link>
      </div>

      <!-- Pagination footer -->
      <section-pagination-footer
        :show="showPagination"
        :text="paginationText"
        :can-prev="canGoToPreviousPage"
        :can-next="canGoToNextPage"
        @prev="goToPreviousPage"
        @next="goToNextPage"
      />
    </div>
  </v-card>
</template>

<script>
import PaginationMixin from '../mixins/PaginationMixin.js';
import ExpiringContractsTimeframeMixin from '../mixins/ExpiringContractsTimeframeMixin.js';
import SectionChipFilterFeedbackMixin from '../mixins/SectionChipFilterFeedbackMixin.js';
import SectionHeader from '../common/SectionHeader.vue';
import SectionPaginationFooter from '../common/SectionPaginationFooter.vue';
import { formattedCurrency, formatDateReadable } from '../Utils.js';

/**
 * Builds the base story array for a contract (without final formatting).
 * Can be extended by custom story builders to add additional story elements.
 * 
 * @param {Object} contract - The contract object
 * @param {Function} formatCurrency - Currency formatting function
 * @returns {Array<string>} Array of story strings
 */
export function buildBaseContractStories(contract, formatCurrency) {
  const stories = [];
  const value = contract.value || 0;
  
  const historical = contract.historical || {};
  if (historical.avg_similar_value) {
    const avgValue = historical.avg_similar_value;
    if (avgValue > 0) {
      const percentDiff = Math.round(((value - avgValue) / avgValue) * 100);
      if (value > avgValue * 1.5) {
        stories.push(`${Math.abs(percentDiff)}% higher than typical ${contract.category || 'contracts'} for this buyer`);
      } else if (value < avgValue * 0.5) {
        stories.push(`${Math.abs(percentDiff)}% lower than usual for this buyer`);
      }
    }
  }
  
  return stories;
}

export default {
  name: 'BaseExpiringContracts',
  
  mixins: [PaginationMixin, ExpiringContractsTimeframeMixin, SectionChipFilterFeedbackMixin],

  components: {
    SectionHeader,
    SectionPaginationFooter
  },

  props: {
    contracts: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    sectionStyle: {
      type: String,
      default: ''
    },
    isVisible: {
      type: Boolean,
      default: true
    },
    itemsPerPage: {
      type: Number,
      default: 10
    },
    // Customization props
    sectionId: {
      type: String,
      required: true
    },
    baseClass: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      required: true
    },
    headerIcon: {
      type: String,
      required: true
    },
    emptyMessage: {
      type: String,
      default: 'No expiring contracts found'
    },
    // Action button props
    actionButtonText: {
      type: String,
      default: 'Action'
    },
    actionButtonIcon: {
      type: String,
      default: 'mdi-bookmark'
    },
    actionEventName: {
      type: String,
      default: 'action'
    },
    // Story display
    showStory: {
      type: Boolean,
      default: false
    },
    // Story builder function override
    storyBuilder: {
      type: Function,
      default: null
    }
  },

  computed: {
    // Items used by the shared pagination mixin
    paginationItems() {
      return this.filteredContracts || [];
    }
  },

  methods: {
    getContractCardStyle(contract) {
      // Delegate to shared mixin styling based on urgency/timeframe
      return this.getExpiringContractCardStyle
        ? this.getExpiringContractCardStyle(contract)
        : '';
    },

    getDaysLeftColor(contract) {
      // Return the appropriate color class based on timeframe
      if (this.isWithinThreeMonths(contract)) {
        return `${this.baseClass}-expiring-contract-card-days-left-upcoming`;
      } else if (this.isWithinThreeToSixMonths(contract)) {
        return `${this.baseClass}-expiring-contract-card-days-left-future`;
      } else if (this.isWithinSixToTwelveMonths(contract)) {
        return `${this.baseClass}-expiring-contract-card-days-left-long-term`;
      }
      // Default to future for contracts beyond 12 months
      return `${this.baseClass}-expiring-contract-card-days-left-future`;
    },

    /**
     * Returns the timeframe chip configuration for a contract based on expiry timeframe.
     * @param {Object} contract - The contract object
     * @returns {Object|null} Chip configuration with class, icon, and label, or null if no match
     */
    getTimeframeChip(contract) {
      if (this.isWithinThreeMonths(contract)) {
        return {
          class: 'contract-chip-urgent',
          icon: 'mdi-alarm',
          label: 'UPCOMING'
        };
      } else if (this.isWithinThreeToSixMonths(contract)) {
        return {
          class: 'contract-chip-medium',
          icon: 'mdi-clock-alert-outline',
          label: 'FUTURE'
        };
      } else if (this.isWithinSixToTwelveMonths(contract)) {
        return {
          class: 'contract-chip-long-term',
          icon: 'mdi-clock-outline',
          label: 'LONG TERM'
        };
      }
      return null;
    },

    buildContractStory(contract) {
      // Use custom story builder if provided, otherwise use default
      if (this.storyBuilder) {
        return this.storyBuilder(contract, this.formatCurrency.bind(this));
      }
      
      const stories = buildBaseContractStories(contract, this.formatCurrency.bind(this));
      return stories.length > 0 ? `This is a ${stories.join(', ')}.` : '';
    },

    formatCurrency(value) {
      return formattedCurrency(value);
    },

    formatDate(dateStr) {
      return formatDateReadable(dateStr);
    },

    handleAction(contract) {
      this.$emit(this.actionEventName, contract);
    }
  }
};
</script>

<style scoped>
[class*="-expiring-contracts-section-timeframe-select"] {
  max-width: 240px;
}

/* Contract card layout */
[class*="contract-card"] {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

[class*="contract-card"]:hover {
  background-color: var(--expiring-contract-card-hover-bg, #f9fafb) !important;
}

[class*="contract-card-with-border"] {
  border-top: 1px solid #e5e7eb;
}

/* Contract title */
[class*="contract-card-title"] {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

[class*="contract-card-title-link"] {
  font-size: 1.25rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  flex-grow: 1;
  color: #1f2937;
  text-decoration: none;
  cursor: pointer;
  pointer-events: auto;
  position: relative;
  z-index: 1;
}

/* Contract details grid */
[class*="-expiring-contract-detail-label"] {
  color: #6b7280;
  font-size: 11px;
}

[class*="-expiring-contract-detail-value"] {
  color: #1f2937;
  font-size: 13px;
}

[class*="-expiring-contract-detail-link"] {
  color: inherit;
}

/* Urgency / timeframe colors (days left) */
[class*="-expiring-contract-card-days-left-upcoming"] {
  color: #d97706 !important;
}

[class*="-expiring-contract-card-days-left-future"] {
  color: #3b82f6 !important;
}

[class*="-expiring-contract-card-days-left-long-term"] {
  color: #6b7280 !important;
}

/* High value alert */
[class*="-expiring-contract-alert"] {
  background-color: #fed7aa;
  border-radius: 4px;
}

[class*="-expiring-contract-alert-text"] {
  color: #4b5563;
  font-style: italic;
  font-size: 13px;
}

/* Contract story */
[class*="-expiring-contract-story"] {
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 4px;
}

[class*="-expiring-contract-story-text"] {
  color: #4b5563;
  font-style: italic;
  font-size: 13px;
}

/* Contract actions */
[class*="-expiring-contract-actions"] {
  gap: 12px;
}

[class*="-expiring-contract-action-btn"] {
  font-size: 13px;
}
</style>

<style>
/* Pagination styles - global to affect child PaginationControls component */
.pagination-container {
  border-top: 1px solid #e5e7eb !important;
  background-color: #f9fafb !important;
  padding: 16px !important;
  padding-top: 8px !important;
  display: flex !important;
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
}

.pagination-container .pagination-content {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
  flex: 1 1 100% !important;
}

.pagination-info {
  font-size: 0.875rem !important;
  color: #6b7280 !important;
}

.pagination-controls {
  display: flex !important;
  gap: 8px !important;
}

/* Timeframe select dropdown menu alignment */
.timeframe-menu .v-list-item__title {
  text-align: left;
  width: 100%;
}

/* Global contract chip styles (used by slots like Pipeline/Competitor sections) */
.contract-chip {
  height: 22px;
  font-size: 11px;
  margin-right: 8px;
  padding: 0 8px;
}

.contract-chip-icon {
  font-size: 14px;
}

/* Contract chip variants */
.contract-chip-group {
  background-color: #e6f0f5 !important;
  color: #12253a !important;
  font-weight: 500;
}

.contract-chip-group .v-icon {
  color: #12253a !important;
}

.contract-chip-competitor {
  background-color: #ffebf3 !important;
  color: #ff1463 !important;
  font-weight: 600;
}

.contract-chip-competitor .v-icon {
  color: #ff1463 !important;
}

.contract-chip-cluster {
  background-color: #e6f0f5 !important;
  color: #12253a !important;
  font-weight: 500;
  cursor: pointer;
}

.contract-chip-cluster .v-icon {
  color: #12253a !important;
}

.contract-chip-urgent {
  background-color: #fef3c7 !important;
  color: #92400e !important;
  font-weight: 600;
}

.contract-chip-urgent .v-icon {
  color: #92400e !important;
}

.contract-chip-medium {
  background-color: #dbeafe !important;
  color: #1e40af !important;
  font-weight: 500;
}

.contract-chip-medium .v-icon {
  color: #1e40af !important;
}

.contract-chip-long-term {
  background-color: #e6f0f5 !important;
  color: #6b7280 !important;
  font-weight: 500;
}

.contract-chip-long-term .v-icon {
  color: #6b7280 !important;
}

.dashboard-section-chip-filter-scroll-target {
  scroll-margin-top: 96px;
}

.dashboard-section-filter-control-wrap {
  border-radius: 4px;
}

.dashboard-section-filter-control-highlight {
  animation: dashboard-section-filter-control-highlight 1.5s ease-in-out;
}

@keyframes dashboard-section-filter-control-highlight {
  0%,
  100% {
    box-shadow: none;
  }

  50% {
    box-shadow: 0 0 0 3px rgba(255, 20, 99, 0.35);
  }
}
</style>
